import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import SectionSidebar from "../components/assessment/SectionSidebar";
import { EMPTY } from "../components/assessment/constants";
import {
  Section1ClientDetails, Section2BusinessStructure, Section3Geography,
  Section4RegistrationGroups, Section5RestrictivePractices, Section6HighIntensity,
  Section7Cohorts, Section8Documents, Section9Timeline,
} from "../components/assessment/Sections";

export default function AssessmentForm({ mode = "new" }) {
  const { id } = useParams();
  const isEdit = mode === "edit";
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const leadId = searchParams.get("lead") || null;
  const [ref, setRef] = useState({ registration_groups: [], documents: [] });
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    api.get("/reference").then((r) => setRef(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/assessments/${id}`)
      .then((r) => setForm({ ...EMPTY, ...r.data.input }))
      .catch(() => toast.error("Unable to load assessment"))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  useEffect(() => {
    if (isEdit || !leadId) return;
    api.get(`/crm/leads/${leadId}`).then((r) => {
      const l = r.data;
      setForm((f) => ({
        ...f,
        org_name: l.org_name || "", abn: l.abn || "",
        contact_name: l.contact_name || "", contact_phone: l.contact_phone || "",
        contact_email: l.contact_email || "",
        states: l.state ? [l.state] : [],
        registration_groups: l.interested_groups || [],
      }));
      toast.success(`Pre-filled from lead: ${l.org_name}`);
    }).catch(() => {});
  }, [leadId, isEdit]);

  function upd(k, v) { setForm((f) => ({ ...f, [k]: v })); }
  function toggle(k, val) {
    setForm((f) => {
      const arr = f[k] || [];
      return { ...f, [k]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
    });
  }

  const groupedRegs = useMemo(() => {
    const by = {};
    for (const g of ref.registration_groups || []) {
      by[g.category] = by[g.category] || [];
      by[g.category].push(g);
    }
    return by;
  }, [ref]);

  const docsByGroup = useMemo(() => {
    const by = {};
    for (const d of ref.documents || []) {
      by[d.group] = by[d.group] || [];
      by[d.group].push(d);
    }
    return by;
  }, [ref]);

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.org_name.trim()) {
      toast.error("Organisation / client name is required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, worker_count: Number(form.worker_count) || 0 };
      const req = isEdit
        ? api.put(`/assessments/${id}`, payload)
        : api.post("/assessments" + (leadId ? `?lead_id=${leadId}` : ""), payload);
      const { data } = await req;
      toast.success(isEdit ? "Assessment updated" : "Assessment saved");
      nav(`/assessment/${data.id}`);
    } catch {
      toast.error(isEdit ? "Failed to update assessment" : "Failed to save assessment");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <div className="text-sm text-slate-500">Loading assessment…</div>;

  return (
    <div className="grid lg:grid-cols-[220px_1fr] gap-8">
      <SectionSidebar form={form} />

      <form onSubmit={onSubmit} className="space-y-6" data-testid="assessment-form">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">
            {isEdit ? "Edit assessment" : "New assessment"}
          </p>
          <h1 className="text-3xl font-semibold tracking-tight text-[#0D1B2A]">
            {isEdit ? "Update discovery intake" : "Discovery intake"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isEdit
              ? "Changes are tracked with a field-level history."
              : "Complete all sections to generate the audit pathway report."}
          </p>
        </div>

        <Section1ClientDetails form={form} upd={upd} />
        <Section2BusinessStructure form={form} upd={upd} />
        <Section3Geography form={form} toggle={toggle} />
        <Section4RegistrationGroups form={form} toggle={toggle} groupedRegs={groupedRegs} />
        <Section5RestrictivePractices form={form} upd={upd} />
        <Section6HighIntensity form={form} toggle={toggle} />
        <Section7Cohorts form={form} toggle={toggle} />
        <Section8Documents form={form} toggle={toggle} docsByGroup={docsByGroup} />
        <Section9Timeline form={form} upd={upd} />

        <div className="flex justify-end gap-3 sticky bottom-0 py-4 no-print">
          <Button
            type="button" variant="outline"
            onClick={() => nav(isEdit ? `/assessment/${id}` : "/")}
            data-testid="cancel-button"
          >
            Cancel
          </Button>
          <Button
            type="submit" disabled={submitting}
            className="bg-[#1A7F6E] hover:bg-[#136053] text-white"
            data-testid="submit-assessment-button"
          >
            {submitting
              ? (isEdit ? "Saving…" : "Computing pathway…")
              : (isEdit ? "Save changes" : "Generate audit pathway report")}
          </Button>
        </div>
      </form>
    </div>
  );
}
